import React, {
  useState,
  useRef,
  lazy,
  Suspense,
  useEffect
} from 'react';

import application from 'application';
import serverInterface from 'server-interface';

import useOnClickOutside from 'generics/hooks/on-click-outside';

import SimpleDialog from 'generics/components/simple-dialog';

import userAccessScopes from 'constants/user-access-scopes';

import { ReactComponent as AddIcon } from 'assets/images/add-icon.svg';
import { ReactComponent as ActivateStartupIcon } from 'assets/images/activate-startup-icon.svg';
import { ReactComponent as DeactivateStartupIcon } from 'assets/images/deactivate-startup-icon.svg';
import { ReactComponent as DownArrowIcon } from 'assets/images/down-arrow.svg';

import TextLogo from './text-logo/react';

import { loadModule } from 'utilities/dynamic-import';

import './index.scss';

function importStartupForm() {
  return loadModule(function doImportStartupForm() {
    return import('root/startup-form');
  });
}

const StartupForm = lazy(importStartupForm);
const STACK_TOP_CLASS = 'quick-access-elevated';

function StartupSelectBox(props) {

  const { startups, selected, onChange } = props;

  const startupSelectBoxMainReference = useRef();

  const [displayMenu, setDisplayMenu] = useState(false);
  const [displayStartupFormDialog, setDisplayStartupFormDialog] = useState(false);
  const [rootState, setRootState] = useState({
    loadingStartupId: null,
    disabled: false,
    allowedToCreateStartup: false
  });

  useOnClickOutside(startupSelectBoxMainReference, hideMenu);

  useEffect(function onLoad() {

    initialize();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setAllowedToCreateStartup(allowedToCreateStartup) {

    setRootState(function updateRootState(rootState) {
      return { ...rootState, allowedToCreateStartup };
    });

  }

  async function updateCreatStartupFlag() {

    if (!serverInterface.currentUser) {
      return;
    }

    const allowedToCreateStartup = await serverInterface.currentUser.canCreateNewStartups();

    setAllowedToCreateStartup(allowedToCreateStartup);

  }

  async function initialize() {

    await updateCreatStartupFlag();

  }

  function hideMenu() {

    if (displayMenu === false) {
      return;
    }

    setDisplayMenu(false);
    document.body.classList.remove(STACK_TOP_CLASS);
  }

  function setLoading(loadingStartupId) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        loadingStartupId,
        disabled: loadingStartupId !== null
      };

    });

  }

  async function handleStartupClick(startup) {

    if (startup._id === selected._id) {
      hideMenu();
      return;
    }

    const startupId = startup._id;

    setLoading(startupId);

    const startupWithCode = await serverInterface.currentUser.getStartupWithAccessCodeByOwnerId(startupId);

    onChange(startupWithCode);

    hideMenu();

    setLoading(null);

  }

  function showStartupFormDialog() {

    if (import.meta.env.NODE_ENV === 'development') {
      hideMenu();
      setDisplayStartupFormDialog(true);
      return;
    }

    window.open('https://venturz.co/new-startup', '_blank');

  }

  function toggleStartupsMenu() {

    if (displayMenu === false) {
      setDisplayMenu(true);
      document.body.classList.add(STACK_TOP_CLASS);
    } else {
      setDisplayMenu(false);
      document.body.classList.remove(STACK_TOP_CLASS);
    }

  }

  function canCreateStartup() {

    return rootState.allowedToCreateStartup && application.accessController.canCreateStartup();

  }

  function renderStartupLogo(data) {

    const iconURL = data.icon.url;

    if (iconURL === 'assets/images/startup-default-logo.svg') {

      const textLogoProperties = {
        text: data.name,
        maximumLetters: 2,
        theme: data.theme
      };

      return <TextLogo {...textLogoProperties} />;

    }

    const startupLogoAttributes = {
      className: 'startup-logo',
      style: {
        backgroundImage: `url(${iconURL})`
      }
    };

    return <span {...startupLogoAttributes}></span>;
  }

  function renderStartupActivateDeactivateButton(startupId, selectedId) {
    const startupButtonAttributes = {
      className: 'startup-activate-deactivate-button'
    };

    if (startupId === selectedId) {
      return <button {...startupButtonAttributes}>
        <DeactivateStartupIcon />
      </button>;
    }

    return <button {...startupButtonAttributes}>
      <ActivateStartupIcon />
    </button>;
  }

  function renderCreateStartupButton() {

    if (canCreateStartup() === false) {
      return;
    }

    const addStartupButtonAttributes = {
      id: 'add-startup-button',
      className: 'primary-element',
      onClick() {
        showStartupFormDialog();
      }
    };

    return <button {...addStartupButtonAttributes}>
      <AddIcon />
      <span className="dim-text">Create Startup</span>
    </button>;
  }

  function renderStartup(startup, index) {

    const theme = startup.theme;

    const startupDetailsAttributes = {
      className: 'startup-details'
    };

    const startupListItemAttributes = {
      disabled: rootState.disabled,
      className: 'startup-list-item',
      onClick() {
        rootState.disabled === false && handleStartupClick(startup);
      }
    };

    const startupNameElementAttributes = {
      title: startup.name,
      className: 'startup-name ellipsis',
      style: {
        color: theme.colors.primary,
        fontFamily: theme.fontFamily
      }
    };

    const startupEmailElementAttributes = {
      className: 'startup-owner-email ellipsis',
      style: {
        fontFamily: theme.fontFamily
      }
    };

    if (startup._id === rootState.loadingStartupId) {
      startupListItemAttributes.className += ' loading';
      return <li key={index} {...startupListItemAttributes}>
        <ht-spinner size="small" />
      </li>;
    }

    if (startup._id === selected._id) {
      startupListItemAttributes.className += ' selected';
    }

    return <li key={index} {...startupListItemAttributes}>

      {renderStartupLogo(startup)}
      <div className="startup-details-and-action">

        <div {...startupDetailsAttributes}>

          <h5 {...startupNameElementAttributes}>{startup.name}</h5>
          <span {...startupEmailElementAttributes}>{startup.owner.email}</span>

        </div>

        {renderStartupActivateDeactivateButton(startup._id, selected._id)}

      </div>

    </li>;

  }

  function renderStartups() {

    const elements = [];
    const startupsLength = startups.length;

    for (let index = 0; index < startupsLength; index++) {

      const startup = startups[index];
      const element = renderStartup(startup, index);

      elements.push(element);

    }

    const startupListAttributes = {
      id: 'startups-list',
      className: 'helptap-list hide-scrollbar'
    };

    return <ul {...startupListAttributes}>{elements}</ul>;

  }

  function renderStartupsMenu() {

    let display = 'none';

    if (displayMenu) {
      display = 'block';
    }

    const startupsMenuContainerAttributes = {
      id: 'startups-menu-container',
      style: {
        display
      }
    };

    return <div {...startupsMenuContainerAttributes}>

      {renderStartups()}

      {renderCreateStartupButton()}

    </div>;

  }

  function tryRenderMoreStartupsIndicator() {

    return <DownArrowIcon className="more-startups-indicator" />;

  }

  function renderCurrentStartup() {

    const currentStartupHolderAttributes = {
      id: 'current-startup-holder',
      className: 'ellipsis',
      onClick() {}
    };

    const currentStartupContainerAttributes = {
      id: 'current-startup-container',
      className: 'plural',
      onClick() {
        toggleStartupsMenu();
      }
    };

    if (application.isAuthenticated() === false || application.accessScope !== userAccessScopes.HELPTAP) {
      delete currentStartupContainerAttributes.onClick;
      delete currentStartupContainerAttributes.className;
    }

    return <div {...currentStartupContainerAttributes}>
      <button {...currentStartupHolderAttributes}>

        <div id="current-startup-display">
          <div className="startup-logo-and-drop-down">
            {renderStartupLogo(selected)}
            <span className="ellipsis navigation-link-name">{selected.name}</span>
          </div>
          {tryRenderMoreStartupsIndicator()}
        </div>

      </button>
    </div>;

  }

  function renderStartupFormDialog() {

    if (!displayStartupFormDialog) {
      return;
    }

    const dialogProperties = {
      stackTopClass: STACK_TOP_CLASS,
      contentHolderClassName: 'startup-form-container',
      onClose() {
        setDisplayStartupFormDialog(false);
      }
    };

    const startupFormProperties = {
      mode: 'CREATE',
      async onComplete(startup) {
        setDisplayStartupFormDialog(false);
      },
      onClose() {
        setDisplayStartupFormDialog(false);
      }
    };

    return <SimpleDialog {...dialogProperties}>
      <Suspense fallback={<ht-spinner size="small" />}>
        <StartupForm {...startupFormProperties} />
      </Suspense>
    </SimpleDialog>;

  }

  const startupSelectBoxAttributes = {
    id: 'startup-select-box-main',
    ref: startupSelectBoxMainReference
  };

  return <div {...startupSelectBoxAttributes}>

    {renderCurrentStartup()}

    {renderStartupsMenu()}

    {renderStartupFormDialog()}

  </div>;

}

export default StartupSelectBox;